import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  InternalApiAttributeType,
  InternalApiExportFileType,
  InternalApiLogicalOperator,
  InternalApiPerson,
  InternalApiPersonCategory,
  InternalApiPersonSearchQuery,
  InternalApiSearchFilter,
  InternalApiSearchOperator,
  InternalApiSortDirection,
} from '../api-internal';
import PersonSearchForm from './PersonSearchForm';
import SalvPage from '../common/SalvPage';
import { SearchParamKeys } from '../common/searchParamKeys';
import { useQueryPersons } from './useQueryPersons';
import PersonSearchResults from './PersonSearchResults';
import DataLoadingErrorFallback from '../common/errors/DataLoadingErrorFallback';
import { SalvButton, SalvProgressCircle, Size } from '@salvapp/ui-components';
import { personInternalApi } from '../apis';
import useExportDownloader from '../common/useExportDownloader';
import ExportModal from './ExportModal';
import { getCasePath } from '../navigation/helper';
import { useAttachCasePersons, useQueryCasePersonsPage } from '../case/attachments/persons/casePersonQueryActions';
import { useDispatch } from 'react-redux';
import { showFeedback } from '../feedback/feedbackReducer';
import { CASE_PERSONS_SCROLL_NAME, LIMIT } from '../case/attachments/persons/CasePersons';
import domIds from '../common/domIds';
import { Row } from '@tanstack/react-table';

export type PersonSearchQuery = Omit<InternalApiPersonSearchQuery, 'size'>;

export enum GeneralField {
  EXTERNAL_ID = 'externalId',
  TYPE = 'type',
}

export const defaultSearchFilter: InternalApiSearchFilter = {
  query: undefined,
  attributeIdentifier: GeneralField.EXTERNAL_ID,
  attributeType: InternalApiAttributeType.GENERAL,
  operator: InternalApiSearchOperator.EXISTS,
  not: false,
};

export const newSubQueryFilter = {
  ...defaultSearchFilter,
  operator: InternalApiSearchOperator.IS_EXACTLY,
};

export const defaultSearchQuery: PersonSearchQuery = {
  filters: [defaultSearchFilter],
  logicalOperator: InternalApiLogicalOperator.AND,
  category: InternalApiPersonCategory.CUSTOMER,
  page: 0,
  orderBy: undefined,
  direction: undefined,
};

enum ViewType {
  DEFAULT,
  SELECTING_CASE_ATTACHMENTS,
}

export const PersonSearch = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [showExportModal, setShowExportModal] = useState(false);
  const [selectedRows, setSelectedRows] = useState<InternalApiPerson[]>([]);

  const caseHandle = searchParams.get(SearchParamKeys.caseHandle) ?? '';
  const viewType = caseHandle ? ViewType.SELECTING_CASE_ATTACHMENTS : ViewType.DEFAULT;

  useEffect(() => {
    if (viewType === ViewType.SELECTING_CASE_ATTACHMENTS) {
      document.getElementById(domIds.getMainContentId())?.scrollTo(0, 0);
    }
  }, [viewType]);

  useExportDownloader();

  const initialFilterAmount = searchParams.getAll(SearchParamKeys.operator).length;
  const initialValues: PersonSearchQuery = {
    logicalOperator:
      (searchParams.get(SearchParamKeys.logicalOperator) as InternalApiLogicalOperator) ||
      defaultSearchQuery.logicalOperator,
    category: (searchParams.get(SearchParamKeys.category) as InternalApiPersonCategory) || defaultSearchQuery.category,
    filters: initialFilterAmount
      ? Array.from({ length: initialFilterAmount }, (_, index) => {
          const attributeIdentifier = searchParams.getAll(SearchParamKeys.attributeIdentifier)[index];
          const attributeType = searchParams.getAll(SearchParamKeys.attributeType)[index] as InternalApiAttributeType;
          const query = searchParams.getAll(SearchParamKeys.query)[index] || undefined;
          const operator = searchParams.getAll(SearchParamKeys.operator)[index] as InternalApiSearchOperator;
          const not = searchParams.getAll(SearchParamKeys.not)[index] === 'true';

          return {
            attributeIdentifier,
            attributeType,
            query,
            operator,
            not,
          };
        })
      : defaultSearchQuery.filters,
    orderBy: searchParams.get(SearchParamKeys.orderBy) || defaultSearchQuery.orderBy,
    direction:
      (searchParams.get(SearchParamKeys.direction) as InternalApiSortDirection) || defaultSearchQuery.direction,
    page: searchParams.get(SearchParamKeys.page)
      ? Number(searchParams.get(SearchParamKeys.page))
      : defaultSearchQuery.page,
  };

  const [submittedSearch, setSubmittedSearch] = useState<PersonSearchQuery>(initialValues);

  const {
    data: personsPage,
    isError: isPersonSearchError,
    isPending: isPersonSearchPending,
    isFetching: isPersonSearchFetching,
    error: personSearchError,
  } = useQueryPersons(submittedSearch);

  const {
    data: casePersonsPage,
    isError: isCasePersonsPageError,
    error: casePersonsPageError,
  } = useQueryCasePersonsPage({ handle: caseHandle, page: 0, size: LIMIT });

  const canAttachPersonToCase = useMemo(() => {
    const casePersonsIds = casePersonsPage ? new Set(casePersonsPage.content.map((it) => it.person.id)) : new Set();

    return (row: Row<InternalApiPerson>) => !casePersonsIds.has(row.original.id);
  }, [casePersonsPage]);

  if (isPersonSearchError || isCasePersonsPageError) {
    return (
      <DataLoadingErrorFallback
        message={"Could not load Persons' resources"}
        error={[personSearchError, casePersonsPageError]}
      />
    );
  }

  const exportPersons = async (fileType: InternalApiExportFileType) => {
    await personInternalApi.exportPersons({
      internalApiPersonExportQuery: {
        orderBy: submittedSearch.orderBy,
        direction: submittedSearch.direction,
        filters: submittedSearch.filters,
        logicalOperator: submittedSearch.logicalOperator,
        category: submittedSearch.category,
        fileType: fileType,
      },
    });
  };

  const pageActions =
    viewType === ViewType.SELECTING_CASE_ATTACHMENTS ? (
      <CaseActions handle={caseHandle} selectedRows={selectedRows} />
    ) : (
      <SalvButton label={'Export'} onClick={() => setShowExportModal(true)} size={Size.SMALL} />
    );

  return (
    <SalvPage
      title={viewType === ViewType.SELECTING_CASE_ATTACHMENTS ? 'Select Persons to Attach' : 'Persons'}
      breadcrumb={
        viewType === ViewType.SELECTING_CASE_ATTACHMENTS ? { title: 'Case', to: getCasePath(caseHandle) } : undefined
      }
      actions={pageActions}
    >
      <PersonSearchForm
        isPersonSearchFetching={isPersonSearchFetching}
        initialValues={initialValues}
        setSubmittedSearch={setSubmittedSearch}
        setSearchParams={setSearchParams}
      />
      {isPersonSearchPending ? (
        <SalvProgressCircle ariaLabel={'Loading Persons'} />
      ) : (
        <PersonSearchResults
          personsPage={personsPage}
          isPersonSearchFetching={isPersonSearchFetching}
          submittedSearchState={submittedSearch}
          setSubmittedSearch={setSubmittedSearch}
          setSearchParams={setSearchParams}
          selectedRows={viewType === ViewType.SELECTING_CASE_ATTACHMENTS ? selectedRows : undefined}
          setSelectedRows={viewType === ViewType.SELECTING_CASE_ATTACHMENTS ? setSelectedRows : undefined}
          isRowSelectionEnabled={viewType === ViewType.SELECTING_CASE_ATTACHMENTS ? canAttachPersonToCase : undefined}
        />
      )}
      <ExportModal show={showExportModal} setShow={setShowExportModal} exportEntities={exportPersons} />
    </SalvPage>
  );
};

interface CaseActionsProps {
  handle: string;
  selectedRows: InternalApiPerson[];
}

const CaseActions = ({ handle, selectedRows }: CaseActionsProps) => {
  const navigate = useNavigate();
  const navigateToCase = () => navigate(getCasePath(handle), { state: { scrollTo: CASE_PERSONS_SCROLL_NAME } });
  const dispatch = useDispatch();
  const attachCasePersons = useAttachCasePersons();

  const handleAttachToCase = async () => {
    try {
      await attachCasePersons.mutateAsync({
        handle,
        // TODO if 'select all' was enabled then add all ids matching the search
        externalIds: selectedRows.map((person) => person.id),
      });
      dispatch(showFeedback({ message: 'Persons attached to case successfully', success: true }));
      navigateToCase();
    } catch {
      dispatch(showFeedback({ message: 'Failed to attach persons to case', success: false }));
    }
  };

  return (
    <div style={{ display: 'flex', gap: '8px' }}>
      <SalvButton variant='muted' label='Cancel' onClick={navigateToCase} size={Size.SMALL} />
      <SalvButton
        label={`Attach ${selectedRows.length > 0 ? `(${selectedRows.length})` : ''}`}
        onClick={handleAttachToCase}
        size={Size.SMALL}
        disabled={selectedRows.length === 0 || attachCasePersons.isPending}
      />
    </div>
  );
};

export default PersonSearch;
